// ─────────────────────────────────────────────────────────────────────────
// Campus structure API — departments, programmes and a programme's
// subjects. All on the existing `classrooms` function (server:
// classRoomController); creating a subject stays on `subjects`.
// Every call throws an Error whose message is already readable (see
// friendlyError) so screens can show it as-is.
// ─────────────────────────────────────────────────────────────────────────
import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";
import type { InstituteLabels } from "@/lib/instituteLabels";

export type DepartmentProfile = {
    departmentName: string;          // stored key (normalised) — never changes
    displayName: string;             // what people see
    head: { uid: string; name: string | null; eid: string | null } | null;
    hidden: boolean;
    createdBy: string | null;
    createdAt: number | null;
};

export type Programme = {
    id: string;
    name: string;
    departmentName: string;
    singleClassroom: boolean;
    createdAt: number | null;
};

export type ProgrammeSubject = { id: string; subjectCode: string; subjectName: string | null };

export type RegistrySubject = { id: string; subject_code: string; subject_name: string | null };

export type Incharge = { uid: string; name: string; eid: string; email: string };

// Server error codes → words. Labels keep "Programme"/"Class" etc. right
// for the institute type.
export function friendlyError(code: string, L: InstituteLabels): string {
    const map: Record<string, string> = {
        DEPARTMENT_NOT_FOUND: `This ${L.department.toLowerCase()} no longer exists.`,
        PROGRAMME_NOT_FOUND: `This ${L.programme.toLowerCase()} no longer exists.`,
        TEACHER_NOT_FOUND: "That faculty member was not found.",
        DUPLICATE_NAME: `A ${L.programme.toLowerCase()} with this name already exists in this ${L.department.toLowerCase()}.`,
        PROGRAMME_HAS_CLASSROOMS: `${L.classrooms} are still linked to this ${L.programme.toLowerCase()}. Move or delete them first.`,
        PROGRAMME_ALREADY_HAS_CLASSROOM: `This ${L.programme.toLowerCase()} already has its ${L.classroom.toLowerCase()}.`,
        INVALID_NAME: "Enter a name (up to 100 characters).",
        INVALID_DISPLAY_NAME: "Enter a name (up to 100 characters).",
        LINK_NOT_FOUND: `That ${L.subject.toLowerCase()} is not in this ${L.programme.toLowerCase()}.`,
        DUPLICATE_CODE: `A ${L.subject.toLowerCase()} with this code already exists.`,
        RESERVED_CODE: "Codes starting with __class_ are reserved.",
        NOTHING_TO_UPDATE: "Nothing changed.",
    };
    return map[ code ] || code || "Something went wrong. Please try again.";
}

class ApiError extends Error {
    code: string;
    constructor(code: string) { super(code); this.code = code; }
}

async function call(fn: "classrooms" | "subjects", path: string, init: { method?: string; query?: Record<string, string>; body?: unknown } = {}) {
    const qs = init.query ? `?${new URLSearchParams(init.query)}` : "";
    let res: Response;
    try {
        res = await fetch(`${environment.BASE_URL}/${fn}/${path}${qs}`, {
            method: init.method || "GET",
            headers: apiHeaders(),
            body: init.body === undefined ? undefined : JSON.stringify(init.body),
        });
    } catch {
        throw new ApiError("Could not reach the server. Check your connection.");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) throw new ApiError(data?.message || `Request failed (${res.status})`);
    return data;
}

// Wrap a call so its error message is the friendly one.
export async function withLabels<T>(L: InstituteLabels, run: () => Promise<T>): Promise<T> {
    try {
        return await run();
    } catch (err: any) {
        throw new Error(friendlyError(err?.code || err?.message, L));
    }
}

// ── Departments ──────────────────────────────────────────────────────────
export const listDepartments = async (): Promise<DepartmentProfile[]> =>
    (await call("classrooms", "listDepartments")).departments || [];

// headEid: a faculty E-ID, or null to clear. Only the fields given change.
export const updateDepartment = async (body: { departmentName: string; displayName?: string; headEid?: string | null; hidden?: boolean }): Promise<DepartmentProfile> =>
    (await call("classrooms", "updateDepartment", { method: "POST", body })).department;

// ── Programmes ───────────────────────────────────────────────────────────
export const listProgrammes = async (departmentName?: string): Promise<Programme[]> =>
    (await call("classrooms", "listProgrammes", { query: departmentName ? { departmentName } : undefined })).programmes || [];

// singleClassroom: the programme is its one classroom (created with it).
export const createProgramme = async (body: { name: string; departmentName: string; singleClassroom: boolean; incharge?: Incharge | null }) =>
    (await call("classrooms", "manageProgramme", { method: "POST", body })) as { programme: Programme; classroom?: any; warning?: string };

export const updateProgramme = async (body: { id: string; name?: string; singleClassroom?: boolean }): Promise<Programme> =>
    (await call("classrooms", "manageProgramme", { method: "PUT", body })).programme;

export const removeProgramme = async (id: string) =>
    call("classrooms", "manageProgramme", { method: "DELETE", body: { id } });

// ── A programme's subjects (references to the subject registry) ──────────
export const listProgrammeSubjects = async (programmeId: string): Promise<ProgrammeSubject[]> =>
    (await call("classrooms", "listProgrammeSubjects", { query: { programmeId } })).subjects || [];

export const linkProgrammeSubjects = async (programmeId: string, subjectIds: string[]) =>
    (await call("classrooms", "linkProgrammeSubjects", { method: "POST", body: { programmeId, subjectIds } })) as { linked: string[]; failed: { id: string; reason: string }[] };

export const unlinkProgrammeSubject = async (programmeId: string, subjectId: string) =>
    call("classrooms", "unlinkProgrammeSubject", { method: "POST", body: { programmeId, subjectId } });

// Creates the subject in the registry and adds it to the programme.
export const createSubjectInProgramme = async (programmeId: string, subjectCode: string, subjectName: string | null) =>
    call("subjects", "createSubject", { method: "POST", body: { subjectCode, subjectName, programmeId } });

// ── Classroom links ──────────────────────────────────────────────────────
// For each of departmentName / programmeId: leave out = unchanged, null =
// clear, string = set. Setting a programme sets its department; clearing
// the department clears the programme.
export type ClassroomLinks = { departmentName?: string | null; programmeId?: string | null };

export const updateClassroomLinks = async (classRoomId: string, links: ClassroomLinks) =>
    call("classrooms", "manageClassRoom", { method: "PUT", body: { classRoomId, ...links } });

// Up to 500 classrooms; partial success: { updated: ids, failed: [{id, reason}] }.
export const bulkAssignClassrooms = async (classRoomIds: string[], links: ClassroomLinks) =>
    (await call("classrooms", "bulkAssignClassRooms", { method: "POST", body: { classRoomIds, ...links } })) as
        { updated: string[]; failed: { id: string; reason: string }[] };

// One page of a department's classrooms (server search on name / ID), for
// lists that can be long; programmeId narrows it to one programme.
export type ClassroomSummary = { id: string; name: string; programmeId: string | null };
export const listDepartmentClassrooms = async (departmentName: string, query: string, page: number, limit: number, programmeId: string | null = null): Promise<{ classrooms: ClassroomSummary[]; total: number }> => {
    const res = await call("classrooms", "getClassRooms", {
        query: { departmentName, query, page: String(page), limit: String(limit), ...(programmeId ? { programmeId } : {}) },
    });
    return {
        classrooms: (res.classrooms || []).map((c: any) => ({ id: c.id, name: c.name, programmeId: c.programme_id ?? null })),
        total: Number(res.total) || 0,
    };
};

// Classrooms whose old free-text programme is exactly `text` (case as stored).
export const findClassroomsByProgrammeText = async (text: string): Promise<{ id: string; name: string; department_name: string | null; programme_id: string | null }[]> =>
    (await call("classrooms", "getClassRooms", { query: { programme: text, page: "1", limit: "500" } })).classrooms || [];

// The institute's subject registry, for "add existing" — one page at a time
// (server search on code and name; internal subjects never listed).
export const searchSubjects = async (search: string, page = 1, limit = 20): Promise<{ subjects: RegistrySubject[]; total: number }> => {
    const res = await call("subjects", "listSubjects", { query: { search, page: String(page), limit: String(limit) } });
    return { subjects: res.subjects || [], total: Number(res.total) || 0 };
};
